/**
 * Sandbox hub — base-mode picker + community public presets (Modes-like list).
 */
import {
    SANDBOX_BASE_MODES,
    SANDBOX_BASE_MODE_TITLES,
    isSandboxBaseMode,
    type SandboxBaseMode,
    type SandboxConfig,
    normalizeSandboxConfig,
} from "./sandbox-schema";
import { apiPath } from "../lib/apiPath";

export type SandboxCommunityPreset = {
    id: string;
    name: string;
    config: SandboxConfig;
    userId: string;
    displayName: string | null;
};

export type SandboxHubHooks = {
    onSelectBaseMode: (mode: SandboxBaseMode) => void;
    onLoadPublicPreset: (preset: SandboxCommunityPreset) => void;
    /** Best line for this cloud preset only (never the base mode’s ranking best). */
    getPresetBestLine?: (preset: SandboxCommunityPreset) => string;
    onOpenPresetLeaderboard?: (preset: SandboxCommunityPreset) => void;
    playModeCardBeep: () => void;
};

let hooks: SandboxHubHooks | null = null;
let communityMounted = false;
let communitySort: "newest" | "oldest" | "name" = "newest";
let communitySearch = "";
let communityModeFilter = "";
let communityCache: Array<{
    id: string;
    name: string;
    config: SandboxConfig;
    userId: string;
    displayName: string | null;
    updatedAt: string;
    baseMode: string;
}> = [];

const baseGrid = () => document.getElementById("sandboxBaseModeGrid");
const communityGrid = () => document.getElementById("sandboxCommunityGrid");
const communityEmpty = () => document.getElementById("sandboxCommunityEmpty");
const communitySearchEl = () => document.getElementById("sandboxCommunitySearch") as HTMLInputElement | null;
const communitySortEl = () => document.getElementById("sandboxCommunitySort") as HTMLSelectElement | null;
const communityModeEl = () => document.getElementById("sandboxCommunityModeFilter") as HTMLSelectElement | null;

export function mountSandboxBaseModePicker(h: SandboxHubHooks): void {
    hooks = h;
    const grid = baseGrid();
    if (!grid) return;
    grid.replaceChildren();
    for (const id of SANDBOX_BASE_MODES) {
        const card = document.createElement("button");
        card.type = "button";
        card.className = "modeBox sandboxBaseModeCard";
        card.dataset.baseMode = id;
        card.innerHTML = `<h3>${SANDBOX_BASE_MODE_TITLES[id].toUpperCase()}</h3>`;
        card.addEventListener("click", () => {
            hooks?.playModeCardBeep();
            hooks?.onSelectBaseMode(id);
        });
        grid.appendChild(card);
    }
}

function fillModeFilterOptions(): void {
    const sel = communityModeEl();
    if (!sel || sel.dataset.filled === "1") return;
    sel.dataset.filled = "1";
    for (const id of SANDBOX_BASE_MODES) {
        const opt = document.createElement("option");
        opt.value = id;
        opt.textContent = SANDBOX_BASE_MODE_TITLES[id];
        sel.appendChild(opt);
    }
}

function toCommunityPreset(p: (typeof communityCache)[number]): SandboxCommunityPreset {
    return {
        id: p.id,
        name: p.name,
        config: p.config,
        userId: p.userId,
        displayName: p.displayName,
    };
}

function renderCommunityGrid(): void {
    const grid = communityGrid();
    const empty = communityEmpty();
    if (!grid) return;

    let list = [...communityCache];
    const q = communitySearch.trim().toLowerCase();
    if (q) {
        list = list.filter(
            (p) =>
                p.name.toLowerCase().includes(q) ||
                (p.displayName || "").toLowerCase().includes(q) ||
                p.userId.toLowerCase().includes(q) ||
                p.baseMode.toLowerCase().includes(q) ||
                (SANDBOX_BASE_MODE_TITLES[p.baseMode as SandboxBaseMode] || "").toLowerCase().includes(q),
        );
    }
    if (communityModeFilter) {
        list = list.filter((p) => p.baseMode === communityModeFilter);
    }
    list.sort((a, b) => {
        if (communitySort === "name") return a.name.localeCompare(b.name);
        const ta = Date.parse(a.updatedAt) || 0;
        const tb = Date.parse(b.updatedAt) || 0;
        return communitySort === "oldest" ? ta - tb : tb - ta;
    });

    grid.replaceChildren();
    if (list.length === 0) {
        empty?.classList.remove("hidden");
        return;
    }
    empty?.classList.add("hidden");

    for (const p of list) {
        const card = document.createElement("div");
        card.className = "modeBox sandboxPresetPublicCard";
        card.tabIndex = 0;
        card.setAttribute("role", "button");

        const title = document.createElement("h3");
        title.textContent = p.name;
        card.appendChild(title);

        const meta = document.createElement("p");
        meta.className = "sandboxPresetPublicMeta";
        const modeLabel = isSandboxBaseMode(p.baseMode)
            ? SANDBOX_BASE_MODE_TITLES[p.baseMode]
            : p.baseMode;
        meta.textContent = `${modeLabel} · ${p.displayName || p.userId}`;
        card.appendChild(meta);

        const communityPreset = toCommunityPreset(p);
        const bestLine = hooks?.getPresetBestLine?.(communityPreset) || "";
        if (bestLine) {
            const best = document.createElement("p");
            best.className = "sandboxPresetPublicBest modeBoxBest";
            best.textContent = bestLine;
            card.appendChild(best);
        }

        const actions = document.createElement("div");
        actions.className = "sandboxPresetPublicActions";

        const play = document.createElement("button");
        play.type = "button";
        play.className = "btn green";
        play.textContent = "LOAD";
        play.addEventListener("click", (e) => {
            e.stopPropagation();
            hooks?.playModeCardBeep();
            hooks?.onLoadPublicPreset(communityPreset);
        });
        actions.appendChild(play);

        const lb = document.createElement("button");
        lb.type = "button";
        lb.className = "btn";
        lb.textContent = "Leaderboard";
        lb.addEventListener("click", (e) => {
            e.stopPropagation();
            hooks?.playModeCardBeep();
            hooks?.onOpenPresetLeaderboard?.(communityPreset);
        });
        actions.appendChild(lb);

        card.appendChild(actions);

        const open = () => play.click();
        card.addEventListener("click", (e) => {
            if ((e.target as HTMLElement).closest("button")) return;
            open();
        });
        card.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                open();
            }
        });

        grid.appendChild(card);
    }
}

/** Re-paint best lines after cloud/local bests refresh (without refetching the list). */
export function rerenderSandboxCommunityBestLines(): void {
    if (!communityMounted) return;
    renderCommunityGrid();
}

export async function refreshSandboxCommunityPresets(): Promise<void> {
    fillModeFilterOptions();
    const empty = communityEmpty();
    if (empty) {
        empty.textContent = "Loading public presets…";
        empty.classList.remove("hidden");
    }
    try {
        const modeQ = communityModeFilter ? `&baseMode=${encodeURIComponent(communityModeFilter)}` : "";
        const r = await fetch(apiPath(`/api/sandbox-presets?public=1&limit=100${modeQ}`), {
            credentials: "include",
        });
        if (!r.ok) {
            communityCache = [];
            if (empty) empty.textContent = "Could not load public presets.";
            renderCommunityGrid();
            return;
        }
        const data = (await r.json()) as {
            presets?: Array<{
                id: string;
                name: string;
                config: unknown;
                updatedAt: string;
                userId: string;
                displayName?: string | null;
            }>;
        };
        communityCache = (data.presets || []).map((row) => {
            const cfg = normalizeSandboxConfig(row.config);
            return {
                id: row.id,
                name: row.name,
                config: cfg,
                userId: row.userId,
                displayName: row.displayName ?? null,
                updatedAt: row.updatedAt,
                baseMode: cfg.baseMode,
            };
        });
        renderCommunityGrid();
    } catch {
        communityCache = [];
        if (empty) empty.textContent = "Could not load public presets.";
        renderCommunityGrid();
    }
}

export function mountSandboxCommunityScreen(h: SandboxHubHooks): void {
    hooks = h;
    if (communityMounted) return;
    communityMounted = true;
    fillModeFilterOptions();

    communitySearchEl()?.addEventListener("input", () => {
        communitySearch = communitySearchEl()?.value || "";
        renderCommunityGrid();
    });
    communitySortEl()?.addEventListener("change", () => {
        const v = communitySortEl()?.value;
        communitySort = v === "oldest" || v === "name" ? v : "newest";
        renderCommunityGrid();
    });
    communityModeEl()?.addEventListener("change", () => {
        communityModeFilter = communityModeEl()?.value || "";
        void refreshSandboxCommunityPresets();
    });
}
